import { ApiError } from 'src/types/interfaces/api-error';
import {
  fetchDailyBookingStatistics,
  fetchMonthlyBookingStatistics,
} from '../lib/booking-statistics';
import { BookingStatistics } from '../types/response/booking-statistics.response';
import { useQuery } from '@tanstack/react-query';
import { bookingStatisticsKeys } from '../query-keys/booking-statistics';
import { useUserStore } from 'src/store/user-store';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { StatisticsVariant } from '../../types/statistic-variant';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { MeiliSearchSearchState } from 'src/hooks/meilisearch/types/meilisearch-search-state';
import {
  FilterOperator,
  FilterValue,
} from 'src/hooks/meilisearch/types/filter-value';

export const useBookingStatistics = (
  from: string | null,
  to: string | null,
  variant: StatisticsVariant,
  enabled: boolean = true,
  types?: SpaceType[],
  floors?: number[],
) => {
  const { adminToken } = useUserStore();

  const navigate = useNavigate();

  const location = useLocation();

  const handleChangeSort = useCallback(
    (
      newSort: string[],
      defaultSearchStateConfig?: Partial<MeiliSearchSearchState>,
    ) => {
      navigate({
        to: location.pathname,
        search: {
          ...defaultSearchStateConfig,
          sort: newSort,
          filter: location?.search?.filter ?? {},
        },
        replace: true,
      });
    },
    [location.pathname, location?.search],
  );

  const handleChangeFilterValue = useCallback(
    (
      filterKey: string,
      filterValue: FilterValue,
      filterOperator?: FilterOperator | undefined,
    ) => {
      const filtersCopy = { ...(location.search?.filter || {}) };

      filtersCopy[filterKey] = {
        operator: filtersCopy[filterKey]?.operator ?? filterOperator,
        value: filterValue,
      };

      if (
        !filterValue ||
        (Array.isArray(filterValue) && filterValue?.length === 0)
      ) {
        delete filtersCopy[filterKey];
      }

      navigate({
        to: location.pathname,
        search: {
          sort: location.search?.sort ?? [],
          filter: filtersCopy,
        },
        replace: true,
      });
    },
    [location.search],
  );

  const { data, error, isFetching, isLoading } = useQuery<
    BookingStatistics,
    ApiError
  >({
    queryKey: bookingStatisticsKeys.list(
      adminToken || '',
      from,
      to,
      variant,
      types,
      floors,
    ),
    queryFn: () =>
      variant === 'DAILY'
        ? fetchDailyBookingStatistics(from, to, types, floors)
        : fetchMonthlyBookingStatistics(from, to, types, floors),
    enabled,
  });

  return {
    data,
    error,
    isFetching,
    isLoading,
    handleChangeFilterValue,
    handleChangeSort,
  };
};
